import {
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { Layout } from "./components/Layout.tsx";
import { withMessages } from "./i18n.ts";
import { api } from "./api.ts";
import { configQuery } from "./config.tsx";
import { workspacesQuery } from "./workspace/data.ts";
import { PageView } from "./workspace/PageView.tsx";
import { WorkspaceLayout } from "./workspace/WorkspaceLayout.tsx";
import { safeNext } from "./next.ts";
import { addressBarPath, centralHref, handoverHref, leaveFor, routerPath } from "./addresses.ts";

interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => (
    <Layout>
      <Outlet />
    </Layout>
  ),
});

/**
 * Guards read the bootstrap config from the query cache, not from React state, so they see a
 * refetch (after setup, sign-in or sign-out) immediately, before the next render.
 */
const getConfig = (context: RouterContext) => context.queryClient.ensureQueryData(configQuery);

async function requireSetupDone({ context }: { context: RouterContext }) {
  const config = await getConfig(context);
  if (config.setupRequired) throw redirect({ to: "/setup" });
  return config;
}
type GuardArgs = { context: RouterContext; location?: { href: string } };

async function requireUser(args: GuardArgs) {
  const config = await requireSetupDone(args);
  // After signing in, the person comes back here (an app path only: see `safeNext`).
  if (!config.user) {
    const next = args.location?.href;
    // On a workspace address, through the central host's sign-in and pass (ADR-0098 §4).
    if (config.address) {
      await leaveFor(handoverHref(config.centralOrigin, `${location.origin}${next ?? "/"}`));
    }
    throw redirect({ to: "/sign-in", search: next && next !== "/" ? { next } : {} });
  }
  return config;
}

/** A path to return to after signing in (next.ts); anything else is ignored. */
export { safeNext };

/** Pages without a sidebar: the content area is the whole <main>. */
const plainRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_plain",
  component: () => (
    <main id="main" tabIndex={-1}>
      <Outlet />
    </main>
  ),
});

/** "/" opens the user's first workspace, or offers to create one. */
const homeRoute = createRoute({
  getParentRoute: () => plainRoute,
  path: "/",
  beforeLoad: async (args) => {
    const config = await requireUser(args);
    // A workspace address opens its own workspace (ADR-0098).
    if (config.address) {
      throw redirect({
        to: "/w/$workspaceId",
        params: { workspaceId: config.address.workspaceId },
      });
    }
    const [first] = await args.context.queryClient.ensureQueryData(workspacesQuery);
    // Opening the app: the start page chosen (ADR-0089): Home, a page, or the last page opened.
    if (first) {
      let startPage: unknown;
      try {
        startPage = (JSON.parse(config.user?.prefs ?? "{}") as { startPage?: unknown }).startPage;
      } catch {
        startPage = undefined;
      }
      if (startPage === "last") {
        const last = await api<{ recents: { id: string }[] }>(
          `/workspaces/${encodeURIComponent(first.id)}/home`,
        )
          .then((home) => home.recents[0]?.id)
          .catch(() => undefined);
        if (last) startPage = last;
      }
      if (typeof startPage === "string" && startPage !== "home" && startPage !== "last") {
        throw redirect({
          to: "/w/$workspaceId/p/$pageId",
          params: { workspaceId: first.id, pageId: startPage },
        });
      }
      throw redirect({
        to: "/w/$workspaceId",
        params: { workspaceId: first.id },
      });
    }
  },
  component: lazyRouteComponent(() => import("./pages/HomePage.tsx"), "HomePage"),
});

const newWorkspaceRoute = createRoute({
  getParentRoute: () => plainRoute,
  path: "/new-workspace",
  beforeLoad: requireUser,
  component: lazyRouteComponent(() => import("./pages/HomePage.tsx"), "HomePage"),
});

/** "Shared with me": pages shared with the viewer in every workspace (readers' home). */
const sharedRoute = createRoute({
  getParentRoute: () => plainRoute,
  path: "/shared",
  beforeLoad: requireUser,
  component: lazyRouteComponent(
    withMessages(() => import("./pages/SharedPage.tsx")),
    "SharedPage",
  ),
});

/** Commenting on a published page, from its site (ADR-0014 §7): on the app's own domain. */
const commentRoute = createRoute({
  getParentRoute: () => plainRoute,
  path: "/comment/$pageId",
  beforeLoad: requireUser,
  component: lazyRouteComponent(
    withMessages(() => import("./pages/CommentPage.tsx")),
    "CommentPage",
  ),
});

/** "Duplicate as template", from a published page's site (ADR-0092): on the app's own domain. */
const templateRoute = createRoute({
  getParentRoute: () => plainRoute,
  path: "/template/$pageId",
  beforeLoad: requireUser,
  component: lazyRouteComponent(
    withMessages(() => import("./pages/TemplatePage.tsx")),
    "TemplatePage",
  ),
});

/** The inbox (ADR-0022): replies, proposed edits and reports. */
const inboxRoute = createRoute({
  getParentRoute: () => plainRoute,
  path: "/inbox",
  beforeLoad: requireUser,
  component: lazyRouteComponent(
    withMessages(() => import("./pages/InboxPage.tsx")),
    "InboxPage",
  ),
});

/**
 * A workspace with an address of its own opens there (ADR-0098 stage 2, address-routes.ts),
 * when it is not this one.
 */
async function openAtAddress(
  args: GuardArgs & {
    params: { workspaceId: string };
    location: { pathname: string; searchStr?: string };
  },
) {
  const config = await requireUser(args);
  if (!config.openAtAddresses || config.address?.workspaceId === args.params.workspaceId) return;
  await (await import("./address-routes.ts")).leaveForAddress(args, config);
}

const workspaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/w/$workspaceId",
  beforeLoad: openAtAddress,
  component: WorkspaceLayout,
});

/**
 * A readable address on a workspace address (ADR-0098 stage 2), looked up: the page is shown,
 * the address bar keeping (or moving to) the page's current address. Nothing there for this
 * person: the app's "not found".
 */
const addressLookupRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/a/$",
  beforeLoad: async ({ params, location }) =>
    (await import("./address-routes.ts")).lookUpAddress(params, location),
  component: lazyRouteComponent(
    withMessages(() => import("./workspace/AddressNotFound.tsx")),
    "AddressNotFound",
  ),
});

const workspaceHomeRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/",
  // `templates`: the gallery is open.
  validateSearch: (search: Record<string, unknown>): { templates?: 1 } => ({
    ...(search.templates === 1 || search.templates === "1" ? { templates: 1 as const } : {}),
  }),
  component: lazyRouteComponent(
    withMessages(() => import("./workspace/WorkspaceHome.tsx")),
    "WorkspaceHome",
  ),
});

const pageRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/p/$pageId",
  // `?new` marks a page just created, whose title field takes focus.
  // `?share` opens the Share dialog (from an access request in the inbox).
  // `?view` opens a database on one of its views, `?peek` a row beside it (ADR-0094).
  validateSearch: (
    search: Record<string, unknown>,
  ): { new?: true; share?: true; view?: string; peek?: string } => ({
    ...(search.new === true || search.new === "true" ? { new: true as const } : {}),
    ...(search.share === true || search.share === "true" ? { share: true as const } : {}),
    ...(typeof search.view === "string" ? { view: search.view } : {}),
    ...(typeof search.peek === "string" ? { peek: search.peek } : {}),
  }),
  component: PageView,
});

const trashRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/trash",
  component: lazyRouteComponent(
    withMessages(() => import("./workspace/TrashPage.tsx")),
    "TrashPage",
  ),
});

const teamspacesRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/teamspaces",
  component: lazyRouteComponent(
    withMessages(() => import("./workspace/TeamspacePages.tsx")),
    "TeamspacesPage",
  ),
});

const teamspaceRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/teamspaces/$teamspaceId",
  component: lazyRouteComponent(
    withMessages(() => import("./workspace/TeamspacePages.tsx")),
    "TeamspacePage",
  ),
});

const archiveRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/archive",
  component: lazyRouteComponent(
    withMessages(() => import("./workspace/ArchivePage.tsx")),
    "ArchivePage",
  ),
});

const libraryRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/library",
  component: lazyRouteComponent(
    withMessages(() => import("./workspace/LibraryPage.tsx")),
    "LibraryPage",
  ),
});

const filesRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/files",
  component: lazyRouteComponent(
    withMessages(() => import("./workspace/FilesPage.tsx")),
    "FilesPage",
  ),
});

const peopleRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/people",
  component: lazyRouteComponent(
    withMessages(() => import("./workspace/PeoplePage.tsx")),
    "PeoplePage",
  ),
});

const searchRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/search",
  component: lazyRouteComponent(
    withMessages(() => import("./workspace/SearchPage.tsx")),
    "SearchPage",
  ),
});

const connectionsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/settings/connections",
  component: lazyRouteComponent(
    withMessages(() => import("./workspace/ConnectionsPage.tsx")),
    "ConnectionsPage",
  ),
});

const connectionRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/settings/connections/$appId",
  component: lazyRouteComponent(
    withMessages(() => import("./workspace/ConnectionPage.tsx")),
    "ConnectionPage",
  ),
});

/** Settings → Address (ADR-0098): the workspace's addresses, for its owners. */
const addressRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/settings/address",
  component: lazyRouteComponent(
    withMessages(() => import("./workspace/AddressPage.tsx")),
    "AddressPage",
  ),
});

const workspaceSettingsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/settings",
  component: lazyRouteComponent(
    withMessages(() => import("./workspace/WorkspaceSettingsPage.tsx")),
    "WorkspaceSettingsPage",
  ),
});

/** A site's settings (ADR-0053): its workspace's owners and administrators. */
const siteSettingsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/site",
  component: lazyRouteComponent(
    withMessages(() => import("./workspace/SiteSettingsPage.tsx")),
    "SiteSettingsPage",
  ),
});

const setupRoute = createRoute({
  getParentRoute: () => plainRoute,
  path: "/setup",
  beforeLoad: async ({ context }) => {
    if (!(await getConfig(context)).setupRequired) throw redirect({ to: "/" });
  },
  // Rarely used pages are split out of the main bundle.
  component: lazyRouteComponent(
    withMessages(() => import("./pages/SetupPage.tsx")),
    "SetupPage",
  ),
});

const signInRoute = createRoute({
  getParentRoute: () => plainRoute,
  path: "/sign-in",
  validateSearch: (search: Record<string, unknown>): { error?: string; next?: string } => ({
    ...(typeof search.error === "string" ? { error: search.error } : {}),
    ...(safeNext(search.next) ? { next: safeNext(search.next) as string } : {}),
  }),
  beforeLoad: async (args) => {
    const config = await requireSetupDone(args);
    if (config.user) throw redirect({ to: "/" });
    if (config.address) {
      await leaveFor(
        handoverHref(config.centralOrigin, `${location.origin}${args.search.next ?? "/"}`),
      );
    }
  },
  // Loaded when needed: people already signed in never fetch it or its passkey code.
  component: lazyRouteComponent(
    withMessages(() => import("./pages/SignInPage.tsx")),
    "SignInPage",
  ),
});

/**
 * Email (ADR-0076): "check" waits for a sign-in code typed here; "link" is where an email's link
 * lands (sign in, create the account, or confirm a new address); "unsubscribe" stops emails.
 */
const signInEmailRoute = createRoute({
  getParentRoute: () => plainRoute,
  path: "/email/$step",
  validateSearch: (search: Record<string, unknown>): { email?: string } =>
    typeof search.email === "string" ? { email: search.email.slice(0, 254) } : {},
  beforeLoad: requireSetupDone,
  component: lazyRouteComponent(
    withMessages(() => import("./pages/EmailSignInPages.tsx")),
    "EmailSignInPage",
  ),
});

/** A form's own page (ADR-0068): open to whoever its sharing lets answer, signed in or not. */
const formRoute = createRoute({
  getParentRoute: () => plainRoute,
  path: "/f/$token",
  beforeLoad: requireSetupDone,
  component: lazyRouteComponent(
    withMessages(() => import("./pages/FormPage.tsx")),
    "FormPage",
  ),
});

/** An invitation link: open to anyone with the link (decision D1). */
const inviteRoute = createRoute({
  getParentRoute: () => plainRoute,
  path: "/invite/$token",
  beforeLoad: requireSetupDone,
  component: lazyRouteComponent(
    withMessages(() => import("./pages/InvitePage.tsx")),
    "InvitePage",
  ),
});

/** An administrator's recovery link: open to whoever holds it, like an invitation. */
const recoverRoute = createRoute({
  getParentRoute: () => plainRoute,
  path: "/recover/$token",
  beforeLoad: requireSetupDone,
  component: lazyRouteComponent(
    withMessages(() => import("./pages/RecoverPage.tsx")),
    "RecoverPage",
  ),
});

const accountSecurityRoute = createRoute({
  getParentRoute: () => plainRoute,
  path: "/account/security",
  // Passkeys are bound to the central host (ADR-0098 §4): this opens there.
  beforeLoad: async (args) => centralOnly(args, await requireUser(args)),
  component: lazyRouteComponent(
    withMessages(() => import("./pages/AccountSecurityPage.tsx")),
    "AccountSecurityPage",
  ),
});

const accountEditingRoute = createRoute({
  getParentRoute: () => plainRoute,
  path: "/account/editing",
  beforeLoad: requireUser,
  component: lazyRouteComponent(
    withMessages(() => import("./pages/AccountEditingPage.tsx")),
    "AccountEditingPage",
  ),
});

const accountIntegrationsRoute = createRoute({
  getParentRoute: () => plainRoute,
  path: "/account/integrations",
  beforeLoad: requireUser,
  component: lazyRouteComponent(
    withMessages(() => import("./pages/AccountIntegrationsPage.tsx")),
    "AccountIntegrationsPage",
  ),
});

const accountNotificationsRoute = createRoute({
  getParentRoute: () => plainRoute,
  path: "/account/notifications",
  beforeLoad: requireUser,
  component: lazyRouteComponent(
    withMessages(() => import("./pages/AccountNotificationsPage.tsx")),
    "AccountNotificationsPage",
  ),
});

/** Connecting a tool by signing in (ADR-0065): the tool sends the person here. */
const oauthAuthorizeRoute = createRoute({
  getParentRoute: () => plainRoute,
  path: "/oauth/authorize",
  beforeLoad: requireUser,
  component: lazyRouteComponent(
    withMessages(() => import("./pages/OAuthAuthorizePage.tsx")),
    "OAuthAuthorizePage",
  ),
});

/** The reference for Vim keys (ADR-0012): open to anyone, like any help page. */
const vimHelpRoute = createRoute({
  getParentRoute: () => plainRoute,
  path: "/help/vim",
  component: lazyRouteComponent(
    withMessages(() => import("./pages/VimHelpPage.tsx")),
    "VimHelpPage",
  ),
});

/** Opens at the central host when this is a workspace address (ADR-0098 §5). */
async function centralOnly(args: GuardArgs, config: Awaited<ReturnType<typeof requireUser>>) {
  const there = centralHref(config, args.location?.href ?? "/");
  if (there) await leaveFor(there);
  return config;
}

async function requireAdmin(args: GuardArgs) {
  const config = await centralOnly(args, await requireUser(args));
  if (!config.user?.isInstanceAdmin) throw redirect({ to: "/" });
}

/** Admin → Users, Workspaces, Usage, Jobs, Backups and Content search (ADR-0090). */
const adminInstanceRoute = createRoute({
  getParentRoute: () => plainRoute,
  path: "/admin/$section",
  beforeLoad: async (args) => {
    await requireAdmin(args);
    if (!/^(users|workspaces|usage|jobs|backups|search)$/.test(args.params.section)) {
      throw redirect({ to: "/admin/settings" });
    }
  },
  component: lazyRouteComponent(
    withMessages(() => import("./pages/AdminInstancePage.tsx")),
    "AdminInstancePage",
  ),
});

/** Admin → Workspaces → Addresses (ADR-0098). */
const adminAddressesRoute = createRoute({
  getParentRoute: () => plainRoute,
  path: "/admin/workspaces/addresses",
  beforeLoad: requireAdmin,
  component: lazyRouteComponent(
    withMessages(() => import("./pages/AdminAddressesPage.tsx")),
    "AdminAddressesPage",
  ),
});

const adminSettingsRoute = createRoute({
  getParentRoute: () => plainRoute,
  path: "/admin/settings",
  beforeLoad: requireAdmin,
  component: lazyRouteComponent(
    withMessages(() => import("./pages/AdminSettingsPage.tsx")),
    "AdminSettingsPage",
  ),
});

/** Admin → Audit log (ADR-0060). */
const adminAuditRoute = createRoute({
  getParentRoute: () => plainRoute,
  path: "/admin/audit",
  beforeLoad: requireAdmin,
  component: lazyRouteComponent(
    withMessages(() => import("./pages/AdminAuditPage.tsx")),
    "AdminAuditPage",
  ),
});

/** Admin → Cookies (ADR-0085): what the installation stores and who else can see a visit. */
const adminCookiesRoute = createRoute({
  getParentRoute: () => plainRoute,
  path: "/admin/cookies",
  beforeLoad: requireAdmin,
  component: lazyRouteComponent(
    withMessages(() => import("./pages/AdminCookiesPage.tsx")),
    "AdminCookiesPage",
  ),
});

/** Admin → Appearance (ADR-0053): the app's look for everyone. */
const adminAppearanceRoute = createRoute({
  getParentRoute: () => plainRoute,
  path: "/admin/appearance",
  beforeLoad: requireAdmin,
  component: lazyRouteComponent(
    withMessages(() => import("./pages/AdminAppearancePage.tsx")),
    "AdminAppearancePage",
  ),
});

/** Instance administrators, and the translators and publishers of a language (ADR-0016). */
async function requireTranslator(args: GuardArgs) {
  const user = (await centralOnly(args, await requireUser(args))).user;
  if (!user?.isInstanceAdmin && !user?.translates.length) throw redirect({ to: "/" });
}

async function requireGroupManager(args: GuardArgs) {
  if (!(await centralOnly(args, await requireUser(args))).user?.managesGroups) {
    throw redirect({ to: "/" });
  }
}

/** Admin → Groups (ADR-0019): administrators and the groups' managers. */
const adminGroupsRoute = createRoute({
  getParentRoute: () => plainRoute,
  path: "/admin/groups",
  beforeLoad: requireGroupManager,
  component: lazyRouteComponent(
    withMessages(() => import("./pages/AdminGroupsPage.tsx")),
    "AdminGroupsPage",
  ),
});

/** Admin → Translations (ADR-0016): the languages, then one language's messages. */
const adminTranslationsRoute = createRoute({
  getParentRoute: () => plainRoute,
  path: "/admin/translations",
  beforeLoad: requireTranslator,
  component: lazyRouteComponent(
    withMessages(() => import("./pages/AdminTranslationsPage.tsx")),
    "AdminTranslationsPage",
  ),
});

const adminLanguageRoute = createRoute({
  getParentRoute: () => plainRoute,
  path: "/admin/translations/$tag",
  beforeLoad: requireTranslator,
  component: lazyRouteComponent(
    withMessages(() => import("./pages/AdminLanguagePage.tsx")),
    "AdminLanguagePage",
  ),
});

const adminLocaleDataRoute = createRoute({
  getParentRoute: () => plainRoute,
  path: "/admin/translations/$tag/locale-data",
  beforeLoad: requireTranslator,
  component: lazyRouteComponent(
    withMessages(() => import("./pages/AdminLocaleDataPage.tsx")),
    "AdminLocaleDataPage",
  ),
});

const routeTree = rootRoute.addChildren([
  plainRoute.addChildren([
    homeRoute,
    newWorkspaceRoute,
    sharedRoute,
    commentRoute,
    templateRoute,
    inboxRoute,
    setupRoute,
    signInRoute,
    signInEmailRoute,
    formRoute,
    inviteRoute,
    recoverRoute,
    accountSecurityRoute,
    accountEditingRoute,
    accountIntegrationsRoute,
    accountNotificationsRoute,
    oauthAuthorizeRoute,
    vimHelpRoute,
    adminSettingsRoute,
    adminAppearanceRoute,
    adminAuditRoute,
    adminCookiesRoute,
    adminTranslationsRoute,
    adminGroupsRoute,
    adminLanguageRoute,
    adminLocaleDataRoute,
    adminAddressesRoute,
    adminInstanceRoute,
  ]),
  workspaceRoute.addChildren([
    workspaceHomeRoute,
    pageRoute,
    addressLookupRoute,
    teamspacesRoute,
    teamspaceRoute,
    archiveRoute,
    libraryRoute,
    filesRoute,
    peopleRoute,
    searchRoute,
    trashRoute,
    workspaceSettingsRoute,
    addressRoute,
    connectionsRoute,
    connectionRoute,
    siteSettingsRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  // Readable addresses on a workspace address (ADR-0098 stage 2, addresses.ts).
  rewrite: {
    input: ({ url }) => routerPath(url),
    output: ({ url }) => addressBarPath(url),
  },
  // Supplied by <RouterProvider context> in App.tsx.
  context: { queryClient: undefined as unknown as QueryClient },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
